import type {
  FilterConditionDto,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdCondition
} from '@bindoc-projects/condition-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import { BdArrayUtil, BdEquals } from '@bindoc-projects/util-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const DISTANCE_CONDITION_ID = 'distance';
export const FilterConditionDistancePluginType: FilterConditionPluginSpecification<
  {
    coordinates: [number, number];
    radius: number;
  },
  FilterConditionDistanceDto,
  AysReferralDistanceCondition
> = {
  type: 'distance',
  category: 'clinic',
  displayName: { de: 'Ärzte im Umkreis der Klinik' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(obj: FilterConditionDistanceDto): FilterConditionDistancePlugin {
    return new FilterConditionDistancePlugin(obj.coordinates, obj.radius);
  },
  fromCondition(
    jsonObj: AysReferralDistanceCondition,
  ): FilterConditionDistancePlugin {
    const json: {
      distance: {
        centerCoordinates: {
          x: number;
          y: number;
        };
        radius: number;
      };
    } = jsonObj.toJSON() as {
      distance: {
        centerCoordinates: {
          x: number;
          y: number;
        };
        radius: number;
      };
    };

    return new FilterConditionDistancePlugin(
      [json.distance.centerCoordinates.x, json.distance.centerCoordinates.y],
      json.distance.radius,
    );
  },
  newInstance(): FilterConditionDistancePlugin {
    return new FilterConditionDistancePlugin(undefined, 20);
  },
};

export class FilterConditionDistancePlugin extends FilterConditionPluginImpl<
  {
    coordinates: [number, number];
    radius: number;
  },
  FilterConditionDistanceDto
> {
  public static override Specification = FilterConditionDistancePluginType;

  constructor(
    private coordinates: [number, number],
    private radius: number,
  ) {
    super(FilterConditionDistancePluginType.type);
  }

  override clone(): FilterConditionDistancePlugin {
    return new FilterConditionDistancePlugin(
      this.coordinates ? [...this.coordinates] : undefined,
      this.radius,
    );
  }

  override equals(other: FilterConditionDistancePlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.coordinates, other.coordinates) &&
      BdEquals.equals(this.radius, other.radius)
    );
  }

  override validationToText(): string {
    if (!this.coordinates || this.coordinates.length !== 2) {
      return 'Gültigen Standort auswählen';
    }

    if (!this.radius) {
      return 'Radius auswählen';
    }

    return '';
  }

  override toCondition(): BdCondition {
    return new AysReferralDistanceCondition(this.coordinates, this.radius);
  }

  override toJSON(): FilterConditionDistanceDto {
    return {
      type: this.type,
      radius: this.radius,
      coordinates: this.coordinates,
    };
  }

  override toValue(): {
    coordinates: [number, number];
    radius: number;
  } {
    return {
      coordinates: this.coordinates,
      radius: this.radius,
    };
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items: string[] = [`Radius: ${this.radius}km`];
    if (this.coordinates) {
      items.push(`Koordinaten: ${this.coordinates[0]}, ${this.coordinates[1]}`);
    }
    return { title, items };
  }

  override toText(): string {
    return `Ärzte im Umkreis von ${this.radius}km der Klinik`;
  }

  override valid(): boolean {
    return super.valid() && !!this.radius && !!this.coordinates && this.coordinates.length === 2;
  }
}

class AysReferralDistanceCondition extends BdCondition {
  constructor(
    private coordinates: [number, number],
    private radius: number,
  ) {
    super(DISTANCE_CONDITION_ID);
  }

  isValid(): boolean {
    return !!this.coordinates && !!this.radius;
  }

  toJSON(): FilterConditionDto {
    return {
      distance: {
        centerCoordinates: {
          x: this.coordinates[0],
          y: this.coordinates[1],
        },
        radius: this.radius,
      },
    };
  }
}

interface FilterConditionDistanceDto {
  type: string;
  coordinates: [number, number];
  radius: number;
}
